import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { RootState, useAppSelector, useAppDispatch } from '@/redux/store';
import { CHAT_NAMESPACES } from '@/types/aiChat';
import { CopilotKit } from '@copilotkit/react-core';
import { useNewConversationMutation } from '@/redux/slices/gigawattBackendApiSlice';
import {
  setConversationId,
  restoreConversation,
} from '@/redux/slices/copilotKitChatSlice';
import {
  CopilotKitAgentProvider,
  CopilotKitAgentErrorProvider,
  CopilotKitAgentLoadingProvider,
} from './CopilotKitAgentContext';

type CopilotKitProviderProps = {
  children: React.ReactNode;
};

/**
 * CopilotKitProvider wraps the application with CopilotKit configuration.
 *
 * Four render states — all render children so the app works normally:
 * - Loading (!threadId, no error): CopilotKitAgentLoadingProvider; chat UIs show an inline spinner.
 * - Error: CopilotKitAgentErrorProvider (isRetrying=false); chat UIs show an inline error + retry.
 * - Retrying: CopilotKitAgentErrorProvider (isRetrying=true); chat UIs show a loading state.
 * - Ready (threadId set): renders <CopilotKit> + CopilotKitAgentProvider + children.
 */
export const CopilotKitProvider = ({ children }: CopilotKitProviderProps) => {
  const dispatch = useAppDispatch();
  const [newConversation, { isLoading: isCreatingConversation, isError }] =
    useNewConversationMutation();
  const conversationRequestedRef = useRef(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const accessToken = useAppSelector(
    (state: RootState) => state.loggedUser?.accessToken
  );
  const organizationId = useAppSelector(
    (state: RootState) => state.loggedUser?.organizationId
  );
  const threadId = useAppSelector(
    (state: RootState) =>
      state.copilotKitChat?.conversations[CHAT_NAMESPACES.GIGAWATT_COPILOTKIT]
        ?.conversationId
  );

  // Memoize runtime URL to prevent CopilotKit from re-initializing on every render.
  // Returns null when organizationId isn't available yet — the Ready render state
  // below is only reached after a successful conversation creation, which requires
  // organizationId to be set, so this null path is never actually rendered.
  const copilotKitRuntimeEndpoint = useMemo(
    () =>
      organizationId
        ? `${window._env_?.VITE_GIGAWATT_URL}/organizations/${organizationId}/users/me/conversations/chat`
        : undefined,
    [organizationId]
  );

  // Memoize headers to prevent CopilotKit from re-initializing on every render
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  // Memoize publicApiKey to prevent CopilotKit from re-initializing
  const publicApiKey = useMemo(
    () => window._env_?.VITE_COPILOTKIT_PUBLIC_API_KEY || undefined,
    []
  );

  const createConversation = useCallback(() => {
    if (!organizationId) return;
    conversationRequestedRef.current = true;
    newConversation(organizationId)
      .unwrap()
      .then(conversation => {
        dispatch(
          setConversationId({
            namespaceKey: CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
            conversationId: conversation.conversationId,
          })
        );
        setIsRetrying(false);
      })
      .catch(() => {
        setIsRetrying(false);
        conversationRequestedRef.current = false;
      });
  }, [organizationId, newConversation, dispatch]);

  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    createConversation();
  }, [createConversation]);

  // Persist conversationId to localStorage whenever it changes.
  useEffect(() => {
    if (!organizationId || !threadId) return;
    localStorage.setItem(
      `gigawatt_${organizationId}_conversation_id`,
      threadId
    );
  }, [organizationId, threadId]);

  // Single effect: either restore an existing conversation from localStorage
  // or create a new one. Never both — avoids the race condition where two
  // separate effects fire simultaneously and createConversation() wins because
  // threadId is still undefined when it reads Redux.
  useEffect(() => {
    if (threadId && conversationRequestedRef.current) {
      conversationRequestedRef.current = false;
    }

    if (
      !organizationId ||
      threadId ||
      isCreatingConversation ||
      isError ||
      conversationRequestedRef.current
    ) {
      return;
    }

    const stored = localStorage.getItem(
      `gigawatt_${organizationId}_conversation_id`
    );

    if (stored) {
      dispatch(
        restoreConversation({
          namespaceKey: CHAT_NAMESPACES.GIGAWATT_COPILOTKIT,
          conversationId: stored,
        })
      );
    } else {
      conversationRequestedRef.current = true;
      createConversation();
    }
  }, [
    organizationId,
    threadId,
    isCreatingConversation,
    isError,
    createConversation,
    dispatch,
  ]);

  // Error (or retrying after error): render the app normally so the user can
  // navigate away. Chat components will see isError=true from context and show
  // an inline error with a retry button instead of the chat interface.
  if (isError || isRetrying) {
    return (
      <CopilotKitAgentErrorProvider retry={handleRetry} isRetrying={isRetrying}>
        {children}
      </CopilotKitAgentErrorProvider>
    );
  }

  // Loading: waiting for the initial conversation ID.
  // Don't render <CopilotKit threadId={undefined}> — CopilotKit may make unkeyed requests
  // or generate its own thread ID, causing "conversation not found" errors from Gigawatt.
  // Render children normally so routing and nav are unaffected; chat UIs show an inline spinner.
  if (!threadId) {
    return (
      <CopilotKitAgentLoadingProvider>
        {children}
      </CopilotKitAgentLoadingProvider>
    );
  }

  // Ready: render CopilotKit + the single shared agent provider for all chat components.
  return (
    <CopilotKit
      key={threadId}
      runtimeUrl={copilotKitRuntimeEndpoint}
      headers={headers}
      threadId={threadId}
      publicApiKey={publicApiKey}>
      <CopilotKitAgentProvider>{children}</CopilotKitAgentProvider>
    </CopilotKit>
  );
};
